import React, { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { ALL_COLUMNS, ColumnDef, SECTOR_LABEL, SectorId } from "./columns";
import {
  CALC_INPUT_META,
  CalcInput,
  ColumnSource,
  getColumnMeta,
  SourceBadge,
  SOURCE_META,
} from "./columnMeta";

const SECTORS_ORDER: SectorId[] = [
  "identification",
  "brief",
  "kpis",
  "alert",
  "pacing",
  "others",
];

const SOURCES_ORDER: ColumnSource[] = ["api", "brief", "calc"];

const CALC_INPUTS_ORDER: CalcInput[] = ["api", "brief", "mixed"];

/**
 * Reference of every WIP column: which sector it belongs to, where its value
 * comes from and what it actually means. Opened from the table toolbar.
 */
const ColumnInfoDialog: React.FC<{ columns?: ColumnDef[] }> = ({
  columns = ALL_COLUMNS,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const needle = search.trim().toLowerCase();

  const groups = useMemo(
    () =>
      SECTORS_ORDER.map((sector) => ({
        sector,
        columns: columns.filter((c) => {
          if (c.sector !== sector) return false;
          if (!needle) return true;
          const meta = getColumnMeta(c.id);
          return (
            c.label.toLowerCase().includes(needle) ||
            meta.description.toLowerCase().includes(needle)
          );
        }),
      })).filter((g) => g.columns.length > 0),
    [needle, columns]
  );

  const totalShown = groups.reduce((acc, g) => acc + g.columns.length, 0);

  return (
    <>
      <Tooltip title="Qué es cada columna">
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          aria-label="Información de las columnas"
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          Qué es cada columna
          <IconButton
            onClick={() => setOpen(false)}
            size="small"
            aria-label="Cerrar"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Legend — what the provenance badges mean */}
          <Stack spacing={1} sx={{ mb: 2 }}>
            {SOURCES_ORDER.map((source) => (
              <Stack
                key={source}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <Box sx={{ pt: 0.3 }}>
                  <SourceBadge source={source} />
                </Box>
                <Typography variant="body2" color={theme.palette.grey[700]}>
                  <b>{SOURCE_META[source].label}</b> —{" "}
                  {SOURCE_META[source].help}
                </Typography>
              </Stack>
            ))}

            {/* A calculated column is only diagnosable if you know what it
                feeds on, so the three variants get spelled out. */}
            <Box sx={{ pl: 5 }}>
              {CALC_INPUTS_ORDER.map((input) => (
                <Typography
                  key={input}
                  variant="body2"
                  color={theme.palette.grey[600]}
                  sx={{ lineHeight: 1.5 }}
                >
                  <b>· {CALC_INPUT_META[input].label}</b> —{" "}
                  {CALC_INPUT_META[input].help}
                </Typography>
              ))}
            </Box>
          </Stack>

          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar columna…"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {totalShown === 0 ? (
            <Typography variant="body2" color={theme.palette.grey[600]}>
              Ninguna columna coincide con «{search}».
            </Typography>
          ) : (
            groups.map((group, idx) => (
              <Box key={group.sector}>
                {idx > 0 && <Divider sx={{ my: 2 }} />}
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.grey[700],
                    letterSpacing: 0.6,
                  }}
                >
                  {SECTOR_LABEL[group.sector]}
                </Typography>

                <Stack spacing={1.25} sx={{ mt: 1 }}>
                  {group.columns.map((col) => {
                    const meta = getColumnMeta(col.id);
                    return (
                      <Stack
                        key={col.id}
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                      >
                        <Box sx={{ pt: 0.35 }}>
                          <SourceBadge source={meta.source} />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, lineHeight: 1.4 }}
                          >
                            {col.label}
                            {meta.source === "calc" && meta.inputs ? (
                              <Box
                                component="span"
                                sx={{
                                  ml: 0.75,
                                  fontWeight: 400,
                                  fontSize: 11.5,
                                  color: theme.palette.grey[600],
                                }}
                              >
                                · {CALC_INPUT_META[meta.inputs].label}
                              </Box>
                            ) : null}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={theme.palette.grey[700]}
                            sx={{ lineHeight: 1.45 }}
                          >
                            {meta.description || "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ColumnInfoDialog;
